import { EventSourcePolyfill } from "event-source-polyfill";
import { saveAs } from "file-saver";
import axiosInstance from "../services/axiosInstance";

const toAbsoluteURL = (url) => {
  if (/^https?:\/\//i.test(url)) return url;

  const baseURL = axiosInstance.defaults.baseURL || window.location.origin;
  return new URL(url, `${baseURL.replace(/\/$/, "")}/`).toString();
};

const getDownloadFileName = (headers, fallbackName) => {
  const contentDisposition = headers?.["content-disposition"] || "";
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  const basicMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  const encodedName = utf8Match?.[1] || basicMatch?.[1];

  if (!encodedName) return fallbackName;

  try {
    return decodeURIComponent(encodedName);
  } catch (_error) {
    return encodedName;
  }
};

const getPdfFileName = (headers, fallbackName) => {
  const fileName = getDownloadFileName(headers, fallbackName);
  const baseName = fileName.replace(/\.[^./\\]+$/, "");
  return `${baseName || "import_error_report"}.pdf`;
};

const parseBlobError = async (response) => {
  const contentType = response.headers?.["content-type"] || "";
  if (!contentType.includes("application/json")) return null;

  try {
    return JSON.parse(await response.data.text());
  } catch (_error) {
    return { success: false, error: "Unable to download error report" };
  }
};

export const importJobWorkflowAPI = {
  async getActiveJob() {
    const response = await axiosInstance.get("/api/importjob/active");
    return response.data;
  },

  async validate(formData) {
    const response = await axiosInstance.post(
      "/api/importjob/validate",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data;
  },

  createValidationStream(streamUrl, token, handlers) {
    const eventSource = new EventSourcePolyfill(toAbsoluteURL(streamUrl), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      heartbeatTimeout: 45000,
    });

    const parseEvent = (event, handler) => {
      try {
        handler?.(JSON.parse(event.data));
      } catch (error) {
        handlers.onTransportError?.(error);
      }
    };

    eventSource.addEventListener("progress", (event) =>
      parseEvent(event, handlers.onProgress),
    );
    eventSource.addEventListener("done", (event) =>
      parseEvent(event, handlers.onDone),
    );
    eventSource.addEventListener("ping", () => handlers.onPing?.());
    eventSource.onerror = (event) => {
      if (event?.data) {
        parseEvent(event, handlers.onError);
        return;
      }
      handlers.onTransportError?.(new Error("Validation stream disconnected"));
    };

    return eventSource;
  },

  async submit(formData) {
    const response = await axiosInstance.post(
      "/api/importjob/submit",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data;
  },

  async getJobStatus(jobId) {
    const response = await axiosInstance.get(
      `/api/importjob/status/${jobId}`,
    );
    return response.data;
  },

  async getHistory(module = "") {
    const response = await axiosInstance.get("/api/importjob/history", {
      params: { module, limit: 0, offset: 0, order: 0 },
    });
    return response.data;
  },

  async downloadErrorReport(url, fallbackName = "import_error_report.pdf") {
    const response = await axiosInstance.get(url, {
      responseType: "blob",
      headers: { Accept: "application/pdf" },
    });
    const apiError = await parseBlobError(response);

    if (apiError) {
      const error = new Error(apiError.error || "Unable to download error report");
      error.apiData = apiError;
      throw error;
    }

    const pdfBlob = new Blob([response.data], { type: "application/pdf" });
    saveAs(pdfBlob, getPdfFileName(response.headers, fallbackName));
  },
};
